import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AppState,
  DashboardActions,
  DashboardInstallationState,
  DashboardState,
  DashboardStoreStatusType,
  DashboardTileOrder,
  DashboardTileType,
  GenericStoreStatus,
} from '@store';
import { DashboardDevice, DashboardInstallationData } from '@vi-iot/client';
import { Observable } from 'rxjs';

import { DashboardSelectors } from './dashboard.selectors';

@Injectable({ providedIn: 'root' })
export class DashboardStore {
  state$: Observable<DashboardState> = this.store$.select(DashboardSelectors.getState());
  tilesOrder$: Observable<DashboardTileOrder[]> = this.store$.select(
    DashboardSelectors.getTilesOrder
  );
  loading$: Observable<boolean> = this.store$.select(DashboardSelectors.getTilesOrderLoading);
  reloadGrid$: Observable<boolean> = this.store$.select(DashboardSelectors.getTilesOrderReloadGrid);
  fetchingDashboardStatus$: Observable<GenericStoreStatus> = this.store$.select(
    DashboardSelectors.selectFetchingDashboardStatus
  );
  dashboardColumnsPresence$: Observable<boolean> = this.store$.select(
    DashboardSelectors.selectDashboardColumnsPresence
  );
  isWhatsAppBannerVisible$: Observable<boolean> = this.store$.select(
    DashboardSelectors.selectIsWhatsAppBannerVisible
  );

  constructor(private store$: Store<AppState>) {}

  fetchTilesOrder(): void {
    this.store$.dispatch(DashboardActions.fetchTilesOrder.attempt());
  }

  saveTilesOrder(tilesOrder: DashboardTileOrder[], showToastMsg = true, reloadGrid = false): void {
    this.store$.dispatch(
      DashboardActions.saveTilesOrder.attempt({ tilesOrder, showToastMsg, reloadGrid })
    );
  }

  fetchDashboard(): void {
    this.store$.dispatch(DashboardActions.getDashboard.attempt());
  }

  initInstallationsForMultipleTileTypes(tileTypes: DashboardTileType[], limit = 10): void {
    this.store$.dispatch(
      DashboardActions.initInstallationsForMultipleTileTypes.attempt({
        tileTypes,
        maxItemsPerFetch: limit,
      })
    );
  }

  initInstallationsForSingleTileType(
    tileType: DashboardTileType,
    tab: string = null,
    limit = 10
  ): void {
    this.store$.dispatch(
      DashboardActions.installationsForSingleTileType.init({
        tileType,
        tab,
        maxItemsPerFetch: limit,
      })
    );
  }

  fetchInstallationsForSingleTileType(
    tileType: DashboardTileType,
    tab: string = null,
    limit = 10
  ): void {
    this.store$.dispatch(
      DashboardActions.installationsForSingleTileType.fetch({
        tileType,
        tab,
        maxItemsPerFetch: limit,
      })
    );
  }

  initContractsEndingSoon(limit = 10): void {
    this.store$.dispatch(
      DashboardActions.contractEndingSoon.init({
        maxItemsPerFetch: limit,
      })
    );
  }

  fetchContractsEndingSoon(limit = 10): void {
    this.store$.dispatch(
      DashboardActions.contractEndingSoon.fetch({
        maxItemsPerFetch: limit,
      })
    );
  }

  getStoreStatus(
    tileType: DashboardTileType,
    tabStateName: string = null
  ): Observable<DashboardStoreStatusType> {
    return this.store$.select(DashboardSelectors.getStoreStatus(tileType, tabStateName));
  }

  getInstallationsState(
    tileType: DashboardTileType,
    tabStateName: string = null
  ): Observable<DashboardInstallationState> {
    return this.store$.select(DashboardSelectors.getInstallationsState(tileType, tabStateName));
  }

  getInstallationCounterForTiles(tiles: DashboardTileType[]): void {
    tiles = tiles.filter(
      (tile: DashboardTileType) => tile !== DashboardTileType.InstallationsWithFault
    );

    this.store$.dispatch(
      DashboardActions.countInstallationsForTiles.init({
        tiles,
      })
    );
  }

  getInstallationCounterForTileTab(tile: DashboardTileType, tab: string): void {
    this.store$.dispatch(
      DashboardActions.countInstallationsForTiles.init({
        tiles: [tile],
        tab: tab,
      })
    );
  }

  getColumnInstallationsCounter(name: string): Observable<number> {
    return this.store$.select(DashboardSelectors.getColumnInstallationsCounter(name));
  }

  getColumnInstallations(name: string): Observable<DashboardInstallationData[]> {
    return this.store$.select(DashboardSelectors.getColumnInstallations(name));
  }

  getDashboardDevicesById(id: number): Observable<DashboardDevice[]> {
    return this.store$.select(DashboardSelectors.getDashboardDevicesById(id));
  }

  getWhatsAppBannerInfoStatus(): void {
    this.store$.dispatch(DashboardActions.whatsAppBannerInfo.attempt());
  }

  hideWhatsAppBanner(): void {
    this.store$.dispatch(DashboardActions.whatsAppBannerInfo.updateValue({ data: false }));
  }
}
